import clsx from "clsx";
import {
  SyntheticEvent,
  useState,
  forwardRef,
  RefObject,
  useEffect,
  ChangeEvent,
} from "react";
import { Loading } from "components";
import Sharing from "./Sharing";
import { useRouter } from "next/router";
import style from "./VideoCard.module.scss";
import Image from "components/Image";
import Hls from "hls.js";

const SKIP_TIME = 5;

interface IProps {
  item: Object | null;
  onVideoEnd: Function;
  getLinkAndUpdateShareLinkList: (item: any) => Promise<string>;
  playerRef: RefObject<HTMLVideoElement>;
}

function formatTime(time) {
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60);
  const seconds = Math.floor(time % 60);
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0"
  )}:${String(seconds).padStart(2, "0")}`;
}

const VideoCard = forwardRef<HTMLDivElement, IProps>(
  ({ item, onVideoEnd, getLinkAndUpdateShareLinkList, playerRef }, ref) => {
     const [isLoading, setIsLoading] = useState<boolean>(false);
    const [progress, setProgress] = useState<number>(0);
    const [currentTime, setCurrentTime] = useState<number>(0);
    const [volume, setVolume] = useState<number>(1);
    const [isPreview, setIsPreview] = useState<boolean>(true);

    const router = useRouter();

    useEffect(() => {
      let hls: Hls | null = null;
      if (!playerRef.current || !item) return;

      const initializeVideo = () => {
        if (item.hlsSrc && Hls.isSupported()) {
          hls = new Hls({ debug: true });
          hls.loadSource(item.hlsSrc);
          hls.attachMedia(playerRef.current);
          hls.on(Hls.Events.ERROR, (event, data) => {
            console.error("HLS error:", data);
            // Fallback to item.src if HLS fails
            if (item.src && playerRef.current && playerRef.current.src !== item.src) {
              playerRef.current.src = item.src;
            }
          });
        } else if (item.src) {
          // Fallback to regular src if HLS is not supported or hlsSrc is not available
          playerRef.current.src = item.src;
        }
      };

      initializeVideo();

      return () => {
        if (hls) {
          console.log("Destroying HLS instance");
          hls.destroy();
          hls = null;
        }
        setIsPreview(true);
        setProgress(0);
        setCurrentTime(0);
      };
    }, [item, router]);

    const onLoadStart = (_: SyntheticEvent<HTMLVideoElement, Event>) => {
      setIsLoading(true);
    };

    const onLoadedData = (_: SyntheticEvent<HTMLVideoElement, Event>) => {
      setIsLoading(false);
    };

    const onEnded = (_: SyntheticEvent<HTMLVideoElement, Event>) => {
      onVideoEnd();
      setProgress(0);
    };

    const skip = (value: number) => {
      if (playerRef.current?.currentTime) {
        playerRef.current.currentTime += value;
      }
    };

    const handlePlay = () => {
      setIsPreview(false);
      if (playerRef.current?.paused) {
        playerRef.current.play();
      }
    };

    const forward = () => skip(SKIP_TIME);
    const backward = () => skip(-SKIP_TIME);

    const toggle = () => {
      if (playerRef.current?.paused) {
        playerRef.current.play();
      } else {
        playerRef.current?.pause();
      }
    };

    const handleTimeUpdate = () => {
      const video = playerRef.current;
      const currentTime = video?.currentTime || 0;
      const duration = video?.duration || 0;
      const progressPercentage = duration ? (currentTime / duration) * 100 : 0;
      setProgress(progressPercentage);
      setCurrentTime(currentTime);
    };

    const toggleFullscreen = () => {
      playerRef.current?.requestFullscreen?.();
    };

    const handleVolumeChange = (e: ChangeEvent<HTMLInputElement>) => {
      const newVolume = e.target?.valueAsNumber;
      if ("volume" in playerRef.current) {
        playerRef.current.volume = newVolume;
      }
      setVolume(newVolume);
    };

    const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const progressBar = e.currentTarget;
      const progressBarRect = progressBar.getBoundingClientRect();
      const clickX = e.clientX - progressBarRect.left;
      const progressBarWidth = progressBarRect.width;
      const seekPercentage = (clickX / progressBarWidth) * 100;
      const seekTime =
        (seekPercentage / 100) * (playerRef.current?.duration || 0);
      if (playerRef.current?.currentTime) {
        playerRef.current.currentTime = seekTime;
      }
    };

    const renderVideo = () => {
      if (item?.src) {
        return (
          <div className={clsx("tw-relative", style.VideoCard)}>
            {isLoading && (
              <div className="tw-absolute tw-flex tw-justify-center tw-items-center tw-w-full tw-h-full tw-bg-[rgba(0,0,0,0.2)] tw-z-50">
                <Loading />
              </div>
            )}
            {isPreview && (
              <div className="tw-absolute tw-flex tw-aspect-[1.77] tw-justify-center tw-items-center tw-w-full tw-h-full tw-bg-[rgba(0,0,0,0.2)] tw-z-50">
                <Image
                  src={item?.thumbnail}
                  width={972}
                  height={550}
                  className="tw-w-full tw-h-full"
                  key={item?.thumbnail}
                  alt={item?.title}
                />
                <div className="tw-absolute tw-flex tw-justify-center tw-items-center tw-w-full tw-h-full tw-top-0 tw-left-0 tw-z-50">
                  <button
                    className="tw-w-16 tw-h-16 md:tw-w-20 md:tw-h-20 tw-cursor-pointer tw-border-0 tw-group tw-rounded-full tw-bg-[rgba(17,62,102,0.5)]"
                    onClick={handlePlay}
                  >
                    <svg
                      className="tw-w-full tw-h-full"
                      width="22"
                      height="23"
                      viewBox="0 0 22 23"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clipPath="url(#clip0_16_3784_1)">
                        <path
                          d="M6.41667 6.12283V16.8772C6.41667 17.6976 7.36083 18.1953 8.08683 17.7489L16.9125 12.3717C17.5844 11.9675 17.5844 11.0334 16.9125 10.6173L8.08683 5.25108C7.35992 4.80467 6.41667 5.30242 6.41667 6.12283Z"
                          className="tw-fill-white group-hover:tw-fill-[#cccccc]"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_16_3784_1">
                          <rect
                            className="tw-fill-white group-hover:tw-fill-[#cccccc]"
                            width="22"
                            height="22"
                            transform="translate(0 0.5)"
                          />
                        </clipPath>
                      </defs>
                    </svg>
                  </button>
                </div>
              </div>
            )}
            <video
              // controls
              className="tw-w-full tw-aspect-[1.77]"
              // poster={item?.thumbnail}
              key={item?.id}
              ref={playerRef}
              onLoadStart={onLoadStart}
              onLoadedData={onLoadedData}
              onEnded={onEnded}
              onTimeUpdate={handleTimeUpdate}
            >
              {/* <source src={item?.src} /> */}
            </video>
            <div
              className={clsx(
                "tw-absolute tw-bottom-0 tw-flex-col tw-items-center tw-w-full",
                {
                  "tw-hidden": !playerRef.current?.paused,
                  "tw-flex": playerRef.current?.paused,
                },
                "control-bar"
              )}
            >
              <div
                className="tw-bg-[#FFFFFF]/20 tw-w-[97%] tw-h-[5px] tw-rounded tw-cursor-pointer"
                style={{ direction: "ltr" }}
                onClick={handleProgressBarClick}
              >
                <div
                  className="tw-bg-[#FFCC00] tw-h-full"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <div
                className="tw-flex tw-justify-between tw-items-center tw-w-full"
                dir="ltr"
              >
                <div className="tw-flex tw-items-center">
                  <button
                    name="pause/play"
                    className="tw-bg-zero tw-border-0 tw-ml-3 sm:tw-ml-6 tw-cursor-pointer"
                    onClick={toggle}
                  >
                    {playerRef.current?.paused ? (
                      <svg
                        width="22"
                        height="23"
                        viewBox="0 0 22 23"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_16_3784)">
                          <path
                            d="M6.41667 6.12283V16.8772C6.41667 17.6976 7.36083 18.1953 8.08683 17.7489L16.9125 12.3717C17.5844 11.9675 17.5844 11.0334 16.9125 10.6173L8.08683 5.25108C7.35992 4.80467 6.41667 5.30242 6.41667 6.12283Z"
                            fill="#FBFBFC"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_16_3784">
                            <rect
                              width="22"
                              height="22"
                              fill="white"
                              transform="translate(0 0.5)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    ) : (
                      <svg
                        fill="white"
                        width={22}
                        height={22}
                        viewBox="0 0 1024 1024"
                        version="1.1"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M256 810.666667h170.666667V213.333333h-170.666667v597.333334z m341.333333-597.333334v597.333334h170.666667V213.333333h-170.666667z" />
                      </svg>
                    )}
                  </button>
                  <button
                    name="volume"
                    className="tw-bg-zero tw-border-0 tw-ml-3 tw-cursor-pointer tw-relative tw-group tw-flex tw-items-center tw-bottom-0.5"
                  >
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={volume}
                      onChange={handleVolumeChange}
                      className="group-hover:tw-w-12 tw-w-0 tw-accent-[#FFCC00] tw-overflow-hidden group-hover:tw-overflow-hidden tw-transition-all"
                      style={{ direction: "revert" }}
                    />
                    <svg
                      width="22"
                      height="23"
                      viewBox="0 0 22 23"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="tw-ml-3"
                    >
                      <g clipPath="url(#clip0_16_3798)">
                        <path
                          d="M11 4.27667C10.7164 4.11292 10.367 4.11292 10.0833 4.27667L5.23417 7.375H2.29167C1.78574 7.375 1.375 7.78574 1.375 8.29167V14.7083C1.375 15.2143 1.78574 15.625 2.29167 15.625H5.23417L10.0833 18.6867C10.2309 18.782 10.4027 18.8329 10.5783 18.8333C11.0846 18.8333 11.495 18.4229 11.495 17.9167V5.08333C11.4923 4.74347 11.3018 4.433 11 4.27667ZM17.9392 4.82667C17.5876 4.4624 17.0063 4.45213 16.6421 4.80375C16.2778 5.15537 16.2676 5.73657 16.6192 6.10083C19.5177 9.11674 19.5177 13.8833 16.6192 16.8992C16.4456 17.0713 16.348 17.3056 16.348 17.55C16.348 17.7944 16.4456 18.0287 16.6192 18.2008C16.7928 18.3635 17.0229 18.4522 17.2608 18.4483C17.509 18.4498 17.7471 18.3506 17.9208 18.1733C21.5015 14.4449 21.5015 8.55508 17.9208 4.82667"
                          fill="#E6E6E6"
                        />
                        <path
                          d="M15.0883 7.17333C14.8564 6.92032 14.5026 6.81807 14.1714 6.90836C13.8403 6.99866 13.5874 7.26638 13.516 7.6021C13.4446 7.93782 13.5667 8.28529 13.8325 8.5025C14.6567 9.28167 15.1237 10.3658 15.1237 11.5C15.1237 12.6342 14.6567 13.7183 13.8325 14.4975C13.5667 14.7147 13.4446 15.0622 13.516 15.3979C13.5874 15.7336 13.8403 16.0013 14.1714 16.0916C14.5026 16.1819 14.8564 16.0797 15.0883 15.8267C16.2818 14.7041 16.9585 13.1384 16.9585 11.5C16.9585 9.86158 16.2818 8.29591 15.0883 7.17333Z"
                          fill="#E6E6E6"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_16_3798">
                          <rect
                            width="22"
                            height="22"
                            fill="white"
                            transform="translate(0 0.5)"
                          />
                        </clipPath>
                      </defs>
                    </svg>
                  </button>
                  <button
                    name="backward"
                    className="tw-bg-zero tw-border-0 tw-ml-3 sm:tw-ml-6 tw-cursor-pointer"
                    onClick={backward}
                  >
                    <svg
                      width="22"
                      height="23"
                      viewBox="0 0 22 23"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clipPath="url(#clip0_16_3787)">
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M11.6471 15.8139C11.329 15.8143 11.022 15.6975 10.7845 15.4859C10.404 15.8266 9.85862 15.9115 9.39244 15.7028C8.92627 15.494 8.6265 15.0306 8.62724 14.5198V12.7941C8.62724 12.222 8.85454 11.6734 9.25913 11.2689C9.66372 10.8644 10.2124 10.6373 10.7845 10.6375H12.6565C12.8946 10.6375 13.0877 10.8303 13.0881 11.0684V14.5198C13.0881 15.2341 12.5082 15.8139 11.7939 15.8139H11.6471ZM12.2249 14.5198V11.5H10.7845C10.0698 11.5 9.49041 12.0794 9.49041 12.7941V14.5198C9.49939 14.7515 9.68982 14.9347 9.92168 14.9347C10.1535 14.9347 10.344 14.7515 10.3529 14.5198V13.6566C10.3619 13.425 10.5523 13.2418 10.7842 13.2418C11.0161 13.2418 11.2065 13.425 11.2155 13.6566V14.5198C11.2158 14.7579 11.409 14.9508 11.6471 14.9508H11.7939C12.0318 14.9504 12.2245 14.7577 12.2249 14.5198Z"
                          fill="#E6E6E6"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M3.88235 11.5C4.35871 11.5 4.74488 11.8862 4.74488 12.3625C4.74438 14.0525 5.50488 15.6529 6.81547 16.7198C8.15007 17.7911 9.89675 18.2031 11.5694 17.8412C13.7044 17.3816 15.372 15.7139 15.8316 13.579C16.3466 11.1061 15.1656 8.59031 12.9341 7.40671C12.267 7.05557 11.5359 6.84275 10.7845 6.781V8.50606C10.7837 8.83116 10.6001 9.12816 10.3096 9.27412C10.029 9.40491 9.6998 9.37527 9.44706 9.19647L5.99629 6.60824C5.77905 6.44535 5.6512 6.18967 5.6512 5.91815C5.6512 5.64662 5.77905 5.39095 5.99629 5.22806L9.44706 2.63982C9.7085 2.44428 10.0579 2.41297 10.3499 2.55892C10.642 2.70487 10.8267 3.00311 10.8272 3.32959V5.05529C14.8087 5.2949 17.8666 8.67617 17.706 12.6616C17.5454 16.6471 14.2255 19.7714 10.2377 19.69C6.24981 19.6085 3.06022 16.3512 3.06253 12.3625C3.06229 11.9025 3.42289 11.5231 3.88235 11.5"
                          fill="#E6E6E6"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_16_3787">
                          <rect
                            width="22"
                            height="22"
                            fill="white"
                            transform="translate(0 0.5)"
                          />
                        </clipPath>
                      </defs>
                    </svg>
                  </button>
                  <button
                    name="forward"
                    className="tw-bg-zero tw-border-0 tw-ml-3 sm:tw-ml-6 tw-cursor-pointer"
                    onClick={forward}
                  >
                    <svg
                      width="22"
                      height="23"
                      viewBox="0 0 22 23"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clipPath="url(#clip0_16_3790)">
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M11.6471 15.8139C11.329 15.8143 11.022 15.6975 10.7845 15.4859C10.404 15.8266 9.85862 15.9115 9.39244 15.7028C8.92627 15.494 8.6265 15.0306 8.62724 14.5198V12.7941C8.62724 12.222 8.85454 11.6734 9.25913 11.2689C9.66372 10.8644 10.2124 10.6373 10.7845 10.6375H12.6565C12.8946 10.6375 13.0877 10.8303 13.0881 11.0684V14.5198C13.0881 15.2341 12.5082 15.8139 11.7939 15.8139H11.6471ZM12.2249 14.5198V11.5H10.7845C10.0698 11.5 9.49041 12.0794 9.49041 12.7941V14.5198C9.49939 14.7515 9.68982 14.9347 9.92168 14.9347C10.1535 14.9347 10.344 14.7515 10.3529 14.5198V13.6566C10.3619 13.425 10.5523 13.2418 10.7842 13.2418C11.0161 13.2418 11.2065 13.425 11.2155 13.6566V14.5198C11.2158 14.7579 11.409 14.9508 11.6471 14.9508H11.7939C12.0318 14.9504 12.2245 14.7577 12.2249 14.5198Z"
                          fill="#E6E6E6"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M4.65882 7.73865C5.95851 6.14508 7.86826 5.17131 9.92135 5.05529V3.32959C9.92172 3.00287 10.1065 2.70437 10.3988 2.55837C10.6911 2.41236 11.0407 2.44389 11.3022 2.63982L14.7529 5.22806C14.9702 5.39095 15.098 5.64662 15.098 5.91815C15.098 6.18967 14.9702 6.44535 14.7529 6.60824L11.3022 9.19647C11.0492 9.37539 10.7198 9.40502 10.439 9.27412C10.149 9.12775 9.96567 8.83093 9.96471 8.50606V6.781C8.41564 6.88294 6.97843 7.62346 5.99629 8.82571C4.92499 10.16 4.51275 11.9064 4.87429 13.579C5.33399 15.7139 7.00161 17.3815 9.13647 17.8412C10.8091 18.2031 12.5558 17.7911 13.8904 16.7198C15.2011 15.6529 15.9616 14.0525 15.961 12.3625C15.961 11.8862 16.3472 11.5 16.8235 11.5C17.2999 11.5 17.6861 11.8862 17.6861 12.3625C17.6885 14.3082 16.9167 16.1749 15.5409 17.5507C14.1652 18.9266 12.2986 19.6985 10.3529 19.6963C7.52364 19.6969 4.9463 18.07 3.72989 15.5156C2.51348 12.9611 2.87497 9.93474 4.65882 7.73865Z"
                          fill="#E6E6E6"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_16_3790">
                          <rect
                            width="22"
                            height="22"
                            fill="white"
                            transform="translate(0 0.5)"
                          />
                        </clipPath>
                      </defs>
                    </svg>
                  </button>
                  <span className="tw-text-white tw-ml-3 sm:tw-ml-6 tw-relative tw-bottom-0.5">
                    {formatTime(currentTime || 0)}/
                    {formatTime(playerRef.current?.duration || 0)}
                  </span>
                </div>
                <div>
                  <button
                    name="fullscreen"
                    className="tw-bg-zero tw-border-0 tw-ml-3 sm:tw-ml-6 tw-cursor-pointer"
                    onClick={toggleFullscreen}
                  >
                    <svg
                      width="48"
                      height="49"
                      viewBox="0 0 48 49"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g opacity="0.9" clipPath="url(#clip0_16_3811)">
                        <g clipPath="url(#clip1_16_3811)">
                          <path
                            d="M14.2227 22.0556H16.6671V17.1667H21.556V14.7222H14.2227V22.0556Z"
                            fill="white"
                            stroke="black"
                            strokeOpacity="0.15"
                            strokeWidth="2.44444"
                          />
                          <path
                            d="M14.2227 22.0556H16.6671V17.1667H21.556V14.7222H14.2227V22.0556Z"
                            fill="white"
                          />
                          <path
                            d="M26.4443 14.7222V17.1667H31.3332V22.0556H33.7777V14.7222H26.4443Z"
                            fill="white"
                            stroke="black"
                            strokeOpacity="0.15"
                            strokeWidth="2.44444"
                          />
                          <path
                            d="M26.4443 14.7222V17.1667H31.3332V22.0556H33.7777V14.7222H26.4443Z"
                            fill="white"
                          />
                          <path
                            d="M31.3332 31.8333H26.4443V34.2778H33.7777V26.9444H31.3332V31.8333Z"
                            fill="white"
                            stroke="black"
                            strokeOpacity="0.15"
                            strokeWidth="2.44444"
                          />
                          <path
                            d="M31.3332 31.8333H26.4443V34.2778H33.7777V26.9444H31.3332V31.8333Z"
                            fill="white"
                          />
                          <path
                            d="M16.6671 26.9444H14.2227V34.2778H21.556V31.8333H16.6671V26.9444Z"
                            fill="white"
                            stroke="black"
                            strokeOpacity="0.15"
                            strokeWidth="2.44444"
                          />
                          <path
                            d="M16.6671 26.9444H14.2227V34.2778H21.556V31.8333H16.6671V26.9444Z"
                            fill="white"
                          />
                        </g>
                      </g>
                      <defs>
                        <clipPath id="clip0_16_3811">
                          <rect
                            width="48"
                            height="48"
                            fill="white"
                            transform="translate(0 0.5)"
                          />
                        </clipPath>
                        <clipPath id="clip1_16_3811">
                          <rect
                            width="44"
                            height="48"
                            fill="white"
                            transform="translate(2 0.5)"
                          />
                        </clipPath>
                      </defs>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      }
      if (item?.iframe) {
        return <div dangerouslySetInnerHTML={{ __html: item?.iframe }} />;
      }
      return "";
    };

    return (
      <div>
        <div ref={ref}>{renderVideo()}</div>
        <h1 className="tw-text-[18px] tw-mt-4">{item?.title}</h1>
        <div className="tw-mt-2">
          <Sharing
            item={item}
            getLinkAndUpdateShareLinkList={getLinkAndUpdateShareLinkList}
          />
        </div>
      </div>
    );
  }
);

export default VideoCard;
